import { useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import type { AgentApiAnalysisSalesDetails, AgentApiAnalysisSalesShow } from '@/types/serializers';
import SalesSummaryCard from './SalesSummaryCard';
import SalesTrendChart from './SalesTrendChart';
import type { SalesTrendMode } from './SalesTrendChart';
import SalesSeriesBarChart from './SalesSeriesBarChart';
import SalesDonutChart from './SalesDonutChart';
import SalesSeriesLineChart from './SalesSeriesLineChart';
import SalesDetailTable from './SalesDetailTable';

interface Props {
  sales: AgentApiAnalysisSalesShow;
  details: AgentApiAnalysisSalesDetails | undefined;
  trendMode: SalesTrendMode;
  onTrendModeChange: (mode: SalesTrendMode) => void;
  page: number;
  onPageChange: (page: number) => void;
  insightMetric: 'lead_time' | 'sales';
  onMetricChange: (value: 'lead_time' | 'sales') => void;
}

/**
 * インサイト「売上」ビュー。
 * サマリー / 売上推移 / 担当者別・成約先別・ポジション別の内訳とトレンド / 明細テーブルを表示する。
 */
export default function SalesView({
  sales,
  details,
  trendMode,
  onTrendModeChange,
  onPageChange,
  insightMetric,
  onMetricChange,
}: Props) {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="mt-md space-y-4">
      <section className="flex flex-col gap-lg rounded-sm bg-white p-md shadow-layer1">
        <SalesSummaryCard
          summary={sales.summary}
          insightMetric={insightMetric}
          onMetricChange={onMetricChange}
        />
        <SalesTrendChart trend={sales.trend} mode={trendMode} onModeChange={onTrendModeChange} />
      </section>

      <section className="grid gap-4" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="min-w-0 rounded-md border border-border bg-white p-4">
          <SalesSeriesBarChart title="担当者別売上金額" series={sales.consultant_series} />
        </div>
        <div className="min-w-0 rounded-md border border-border bg-white p-4">
          <SalesDonutChart title="期間内 担当者別内訳" rows={sales.consultant_breakdown} />
        </div>
      </section>

      <section className="rounded-md border border-border bg-white p-4">
        <SalesSeriesBarChart
          title="成約先 売上金額"
          series={sales.company_series}
          legendPosition="right"
        />
      </section>

      {showMore && (
        <section className="grid gap-4" style={{ gridTemplateColumns: '1fr 2fr' }}>
          <div className="min-w-0 rounded-md border border-border bg-white p-4">
            <SalesDonutChart title="ポジション別 売上内訳" rows={sales.position_breakdown} />
          </div>
          <div className="min-w-0 rounded-md border border-border bg-white p-4">
            <SalesSeriesLineChart
              title="ポジション別売上金額トレンド"
              series={sales.position_trend}
            />
          </div>
        </section>
      )}

      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setShowMore((prev) => !prev)}
          className="flex items-center gap-xs rounded-sm px-md py-sm text-body-sm font-bold text-primary hover:bg-primary_lighten"
        >
          {showMore ? (
            <>
              閉じる
              <LuChevronUp className="size-4" />
            </>
          ) : (
            <>
              もっと見る
              <LuChevronDown className="size-4" />
            </>
          )}
        </button>
      </div>

      {details && (
        <section>
          <SalesDetailTable details={details} onPageChange={onPageChange} />
        </section>
      )}
    </div>
  );
}
